import type { ReactNode } from 'react'
import type { CartItem, Product } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

interface CartContextValue {
    items: CartItem[]
    count: number
    subtotal: number
    isOpen: boolean
    openCart: () => void
    closeCart: () => void
    addItem: (product: Product, quantity?: number) => void
    removeItem: (slug: string) => void
    setQuantity: (slug: string, quantity: number) => void
}

const CartContext = createContext<CartContextValue | null>(null)
const STORAGE_KEY = 'anny-cart'

export function CartProvider({ children }: { children: ReactNode }) {
    const [items, setItems] = useState<CartItem[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [hydrated, setHydrated] = useState(false)

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            if (raw) setItems(JSON.parse(raw) as CartItem[])
        } catch {
            // ignore malformed storage
        }
        setHydrated(true)
    }, [])

    useEffect(() => {
        if (!hydrated) return
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
        } catch {
            // storage may be unavailable; cart still works for the session
        }
    }, [items, hydrated])

    const openCart = useCallback(() => setIsOpen(true), [])
    const closeCart = useCallback(() => setIsOpen(false), [])

    const addItem = useCallback((product: Product, quantity = 1) => {
        setItems((prev) => {
            const existing = prev.find((item) => item.slug === product.slug)
            if (existing) {
                return prev.map((item) =>
                    item.slug === product.slug
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                )
            }
            return [
                ...prev,
                {
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    image: product.image,
                    quantity
                }
            ]
        })
        setIsOpen(true)
    }, [])

    const removeItem = useCallback((slug: string) => {
        setItems((prev) => prev.filter((item) => item.slug !== slug))
    }, [])

    const setQuantity = useCallback((slug: string, quantity: number) => {
        setItems((prev) =>
            quantity <= 0
                ? prev.filter((item) => item.slug !== slug)
                : prev.map((item) =>
                      item.slug === slug ? { ...item, quantity } : item
                  )
        )
    }, [])

    const value = useMemo<CartContextValue>(() => {
        const count = items.reduce((total, item) => total + item.quantity, 0)
        const subtotal = items.reduce(
            (total, item) => total + item.price * item.quantity,
            0
        )
        return {
            items,
            count,
            subtotal,
            isOpen,
            openCart,
            closeCart,
            addItem,
            removeItem,
            setQuantity
        }
    }, [items, isOpen, openCart, closeCart, addItem, removeItem, setQuantity])

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
    const context = useContext(CartContext)
    if (!context) throw new Error('useCart must be used within a CartProvider')
    return context
}
